import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, apiFetch } from '../lib/api';
import TravelModal from '../components/TravelModal';
import SendModal from '../components/SendModal';
import { getTravelHero } from '../lib/eventHero';
import { Card, PersonBadge, Btn, fmt12, fmtDate, todayStr, WeatherIcon } from '../components/ui';
import { usePeople } from '../context/PeopleContext';
import { useConfirm } from '../components/ConfirmModal';

function isEventPast(event) {
  const endTime = event.timeEnd || event.timeStart;
  if (!endTime) return false;
  const now = new Date();
  const [h, m] = endTime.split(':').map(Number);
  const eventEnd = new Date();
  eventEnd.setHours(h, m, 0, 0);
  return now > eventEnd;
}

function EventCard({ event, onTap, onDelete }) {
  const { colorMap } = usePeople();
  const persons = (event.person||'').split(',').map(p=>p.trim()).filter(p => p && p.toLowerCase() !== 'everyone');
  const personColors = persons.map(p => {
    const key = Object.keys(colorMap).find(k => k.toLowerCase() === p.toLowerCase());
    return key ? colorMap[key] : null;
  }).filter(Boolean);
  const pc = personColors[0] || '#e6edf3';
  const isColored = personColors.length > 0;
  const barStyle = personColors.length > 1
    ? { background: `linear-gradient(to bottom, ${personColors.join(', ')})` }
    : { background: isColored ? pc : 'var(--border2)' };

  return (
    <div style={{ background:'var(--card)', borderRadius:12, overflow:'hidden', border:'1px solid var(--border)', marginBottom:8, cursor:'pointer' }}>
      <div style={{ display:'flex', alignItems:'stretch' }}>
        <div style={{ width:4, flexShrink:0, ...barStyle }} />
        <div style={{ flex:1, padding:'14px 16px' }} onClick={() => onTap && onTap(event)}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
            <p style={{ fontSize:16, fontWeight:800, color: isColored ? pc : 'var(--text)', margin:0, lineHeight:1.4 }}>
              {event.timeStart
                ? <><span style={{ textDecoration:'underline' }}>{fmt12(event.timeStart)}</span>: {event.title}</>
                : event.title}
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
              {persons.length > 1 && (
                <span style={{ fontSize:11, fontWeight:700, color: pc, background: pc+'22', border:'1px solid '+pc+'44', borderRadius:20, padding:'1px 7px' }}>
                  +{persons.length}
                </span>
              )}
              {onDelete && (
                <button onClick={e => { e.stopPropagation(); onDelete(event.id); }}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:12, padding:'2px 4px', transition:'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='var(--red)'}
                  onMouseLeave={e => e.currentTarget.style.color='var(--text3)'}
                  title="Remove event">
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
          </div>
          {event.location && <p style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}><i className="fa-solid fa-location-dot" style={{ fontSize:10, marginRight:4, color:'var(--text3)' }}></i>{event.location}</p>}
          {event.notes && <p style={{ fontSize:13, color:'var(--text2)', marginTop:4, lineHeight:1.5 }}>{event.notes}</p>}
        </div>
      </div>
    </div>
  );
}

export default function AgentReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { colorMap } = usePeople();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [travelModal, setTravelModal] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    apiFetch(`/sheets/${id}`)
      .then(data => {
        const evts = (data.events || []).sort((a, b) => {
          if (a.nextDay && !b.nextDay) return 1;
          if (!a.nextDay && b.nextDay) return -1;
          if (!a.timeStart && !b.timeStart) return 0;
          if (!a.timeStart) return 1;
          if (!b.timeStart) return -1;
          return a.timeStart.localeCompare(b.timeStart);
        });
        setSheet({ ...data, events: evts });
      })
      .catch(() => navigate('/admin/sheets', { replace: true }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api.agentApprove(id);
      setSheet(s => ({ ...s, status: 'published' }));
    } catch (err) {
      alert(err.message || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!await confirmAction('Remove this event from the sheet?', { title: 'Delete Event', confirmLabel: 'Delete' })) return;
    setDeleting(eventId);
    try {
      await api.deleteEvent(eventId);
      setSheet(s => ({
        ...s,
        events: s.events.filter(e => e.id !== eventId),
        eventIds: (s.eventIds || []).filter(eid => eid !== eventId),
      }));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const handleDiscard = async () => {
    if (!await confirmAction('Discard this AI-generated sheet? This cannot be undone.', { title: 'Discard Sheet', confirmLabel: 'Discard' })) return;
    try {
      await api.deleteSheet(id);
      navigate('/admin/sheets', { replace: true });
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:32, height:32, border:'2.5px solid var(--border)', borderTop:'2.5px solid #8b5cf6', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}></div>
        <p style={{ color:'var(--text3)', fontSize:14 }}>Loading sheet...</p>
      </div>
    </div>
  );

  if (!sheet) return null;

  const artist = sheet.artist;

  return (
    <div style={{ padding:'32px 40px', maxWidth:700, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button onClick={() => navigate('/admin/sheets')}
          style={{ width:36, height:36, borderRadius:10, border:'1px solid var(--border)', background:'var(--card)',
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text2)', fontSize:14 }}>
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg, #8b5cf6, #d946ef)',
              display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:12, color:'#fff' }}></i>
            </div>
            <p style={{ fontSize:20, fontWeight:900, color:'var(--text)', margin:0 }}>Review AI Sheet</p>
          </div>
          <p style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>Review the generated day sheet, make edits, then approve</p>
        </div>
      </div>

      {/* AI badge */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20, padding:'12px 16px',
        background:'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(217,70,239,0.08))',
        border:'1px solid rgba(139,92,246,0.2)', borderRadius:14 }}>
        <i className="fa-solid fa-robot" style={{ fontSize:16, color:'#8b5cf6' }}></i>
        <div style={{ flex:1 }}>
          <p style={{ fontSize:13, fontWeight:700, color:'#8b5cf6', margin:0 }}>AI Generated Draft</p>
          <p style={{ fontSize:12, color:'var(--text3)', margin:0 }}>
            {sheet.events.length} event{sheet.events.length !== 1 ? 's' : ''} created
            {sheet.travel?.length ? ` · ${sheet.travel.length} travel record${sheet.travel.length !== 1 ? 's' : ''}` : ''}
            {sheet.weather ? ` · Weather loaded` : ''}
          </p>
        </div>
        <span style={{ display:'inline-flex', alignItems:'center', gap:5,
          background:'rgba(210,153,34,0.1)', border:'1px solid rgba(210,153,34,0.3)',
          borderRadius:20, padding:'4px 10px', fontSize:11, fontWeight:700, color:'#d2991a' }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background:'#d2991a', display:'inline-block' }}/>
          Draft
        </span>
      </div>

      {/* Sheet card */}
      <Card style={{ padding:'24px 20px', borderRadius:20, overflow:'hidden', marginBottom:20 }}>

        {/* Header info */}
        <div style={{ textAlign:'center', marginBottom:16 }}>
          {artist?.name && (
            <p style={{ fontSize:20, fontWeight:900, color:'var(--text)', marginBottom:2 }}>
              {artist.name} Day Sheet
            </p>
          )}
          <p style={{ fontSize:15, fontWeight:500, color:'var(--text2)', marginBottom:4 }}>
            {fmtDate(sheet.date, {weekday:'long', month:'long', day:'numeric', year:'numeric'})}
          </p>
          {sheet.city && (
            <p style={{ fontSize:13, color:'var(--text2)', marginBottom:4 }}>{sheet.city.replace(', USA','')}</p>
          )}
          {(() => {
            const weathers = [];
            if (sheet.weather) weathers.push({ location: sheet.city ? sheet.city.replace(', USA','').split(',')[0].trim() : null, weather: sheet.weather });
            (sheet.locationWeathers||[]).forEach(lw => weathers.push(lw));
            if (!weathers.length) return null;
            return (
              <p style={{ fontSize:13, color:'var(--text2)', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'center', gap:6, flexWrap:'wrap' }}>
                {weathers.map((lw, i) => (
                  <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                    {i > 0 && <span style={{ color:'var(--text3)', margin:'0 4px' }}>/</span>}
                    <WeatherIcon condition={lw.weather.condition} />
                    <span>{lw.weather.tempHigh}&deg;/{lw.weather.tempLow}&deg;</span>
                    {lw.location && <span style={{ color:'var(--text3)', fontSize:12 }}>{lw.location}</span>}
                  </span>
                ))}
              </p>
            );
          })()}
        </div>

        {/* Notes */}
        {sheet.notes && (
          <div style={{ background:'rgba(210,153,34,0.1)', border:'1px solid rgba(210,153,34,0.3)', borderLeft:'3px solid #d2991a', borderRadius:10, padding:'10px 14px', marginBottom:16 }}>
            <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1px', color:'#d2991a', marginBottom:3 }}><i className="fa-solid fa-thumbtack" style={{ fontSize:11, marginRight:4 }}></i> NOTE</p>
            <p style={{ fontSize:13, color:'var(--text2)', lineHeight:1.5 }}>{sheet.notes}</p>
          </div>
        )}

        {/* Travel buttons */}
        {(sheet.travel||[]).length > 0 && (() => {
          const typeIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
          const typeColors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
          const resolveColor = (name) => {
            if (!name) return null;
            const key = Object.keys(colorMap).find(k => k.toLowerCase() === name.toLowerCase());
            return key ? colorMap[key] : null;
          };
          const getSummary = (r) => {
            if (r.type === 'flight') return `${r.departureAirport||'?'} → ${r.arrivalAirport||'?'}`;
            if (r.type === 'hotel') return (r.hotelName||'Hotel').split(' ').slice(0,3).join(' ');
            if (r.type === 'carservice') return r.carServiceCompany || 'Car Service';
            if (r.type === 'ground') return r.driverName || 'Ground';
            if (r.type === 'car') return r.carCompany || 'Car Rental';
            return `${r.trainFrom||'?'} → ${r.trainTo||'?'}`;
          };
          return (
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:16, flexWrap:'wrap' }}>
              {(sheet.travel||[]).map(r => {
                const names = (r.passenger||'').split(',').map(s=>s.trim()).filter(Boolean);
                const colors = names.map(n => resolveColor(n)).filter(Boolean);
                const c = colors[0] || typeColors[r.type] || '#1E40AF';
                return (
                  <button key={r.id} onClick={() => setTravelModal(r)}
                    style={{ minWidth:90, padding:'10px 12px', borderRadius:14, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4,
                      background:`${c}14`, border:`1.5px solid ${c}33`, cursor:'pointer', transition:'all 0.15s', fontFamily:'inherit' }}>
                    <i className={`fa-solid ${typeIcons[r.type] || 'fa-circle'}`} style={{ fontSize:20, color:c }}></i>
                    <span style={{ fontSize:9, fontWeight:800, color:c, letterSpacing:0.3, maxWidth:100, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {getSummary(r)}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Schedule */}
        <div style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden', marginBottom:16 }}>
          <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>SCHEDULE</p>
            <span style={{ fontSize:11, color:'var(--text3)' }}>{sheet.events.length} event{sheet.events.length !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ padding:'10px 12px' }}>
            {sheet.events.length === 0
              ? <p style={{ color:'var(--text3)', textAlign:'center', padding:'20px 0', fontSize:14 }}>No events</p>
              : sheet.events.map((e, i) => (
                  <React.Fragment key={e.id}>
                    {e.nextDay && (i === 0 || !sheet.events[i-1].nextDay) && (
                      <div style={{ display:'flex', alignItems:'center', gap:10, margin:'12px 0', padding:'0 4px' }}>
                        <div style={{ flex:1, height:1, background:'linear-gradient(to right, var(--border), transparent)' }} />
                        <span style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', whiteSpace:'nowrap' }}>NEXT DAY</span>
                        <div style={{ flex:1, height:1, background:'linear-gradient(to left, var(--border), transparent)' }} />
                      </div>
                    )}
                    <EventCard event={e} onDelete={handleDeleteEvent} />
                  </React.Fragment>
                ))
            }
          </div>
        </div>
      </Card>

      {/* Action buttons — changes based on approval status */}
      {sheet.status === 'published' ? (
        <>
          {/* Post-approval actions */}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16, padding:'12px 16px',
            background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:14 }}>
            <i className="fa-solid fa-circle-check" style={{ fontSize:16, color:'#22c55e' }}></i>
            <p style={{ fontSize:13, fontWeight:700, color:'#22c55e', margin:0, flex:1 }}>Sheet approved and published</p>
          </div>
          <div style={{ display:'flex', gap:10, marginBottom:10 }}>
            <button onClick={() => navigate(`/admin?date=${sheet.date}&artist=${sheet.artistId}`)}
              style={{ flex:1, padding:'14px 20px', borderRadius:14, border:'1px solid var(--border)', background:'var(--card)',
                color:'var(--text)', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-eye" style={{ fontSize:12 }}></i>
              View Sheet
            </button>
            <button onClick={() => navigate(`/admin/sheets/${id}/edit`)}
              style={{ flex:1, padding:'14px 20px', borderRadius:14, border:'1px solid var(--border)', background:'var(--card)',
                color:'var(--text)', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-pen" style={{ fontSize:12 }}></i>
              Edit
            </button>
          </div>
          <div style={{ display:'flex', gap:10, marginBottom:32 }}>
            <button onClick={() => {
                const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                window.open(`${API}/email/preview/${id}?token=${localStorage.getItem('ds_token')}`, '_blank');
              }}
              style={{ flex:1, padding:'14px 20px', borderRadius:14, border:'1px solid var(--border)', background:'var(--card)',
                color:'var(--text)', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-regular fa-envelope" style={{ fontSize:12 }}></i>
              Preview Email
            </button>
            <button onClick={() => {
                const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                window.open(`${API}/email/pdf/${id}?token=${localStorage.getItem('ds_token')}`, '_blank');
              }}
              style={{ flex:1, padding:'14px 20px', borderRadius:14, border:'1px solid var(--border)', background:'var(--card)',
                color:'var(--text)', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-file-pdf" style={{ fontSize:12 }}></i>
              PDF
            </button>
            <button onClick={() => setShowSend(true)}
              style={{ flex:1, padding:'14px 20px', borderRadius:14, border:'none',
                background:'linear-gradient(135deg, #8b5cf6, #d946ef)',
                boxShadow:'0 4px 15px rgba(139,92,246,0.3)',
                color:'#fff', fontSize:14, fontWeight:800, cursor:'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(139,92,246,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 15px rgba(139,92,246,0.3)'; }}>
              <i className="fa-solid fa-paper-plane" style={{ fontSize:12 }}></i>
              Send
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Pre-approval: full action bar like SheetBuilder preview */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:8 }}>
            <button onClick={handleDiscard}
              style={{ padding:'12px 10px', borderRadius:12, background:'var(--card)', border:'1px solid var(--border)', color:'var(--red)', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
              <i className="fa-solid fa-trash" style={{ fontSize:14 }}></i> Discard
            </button>
            <button onClick={() => navigate(`/admin/sheets/${id}/edit`)}
              style={{ padding:'12px 10px', borderRadius:12, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
              <i className="fa-solid fa-pen" style={{ fontSize:14 }}></i> Edit
            </button>
            <button onClick={handleApprove} disabled={approving}
              style={{ padding:'12px 10px', borderRadius:12, background:'linear-gradient(135deg,#22c55e,#16a34a)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:12, fontWeight:800, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4, opacity: approving ? 0.5 : 1, boxShadow:'0 4px 12px rgba(34,197,94,0.25)' }}>
              <i className={`fa-solid ${approving ? 'fa-spinner fa-spin' : 'fa-rocket'}`} style={{ fontSize:14 }}></i> {approving ? 'Publishing...' : 'Publish'}
            </button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:32 }}>
            <button onClick={() => {
              const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
              window.open(`${API}/email/preview/${id}?token=${localStorage.getItem('ds_token')}`, '_blank');
            }}
              style={{ padding:'12px 10px', borderRadius:12, background:'var(--card)', border:'1px solid var(--border)', color:'#a855f7', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-envelope" style={{ fontSize:13 }}></i> Email Preview
            </button>
            <button onClick={() => {
              const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
              window.open(`${API}/email/pdf/${id}?token=${localStorage.getItem('ds_token')}`, '_blank');
            }}
              style={{ padding:'12px 10px', borderRadius:12, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <i className="fa-solid fa-file-pdf" style={{ fontSize:13, color:'#ef4444' }}></i> PDF
            </button>
          </div>
        </>
      )}

      <TravelModal record={travelModal} onClose={() => setTravelModal(null)} />

      {showSend && sheet && (
        <SendModal sheet={sheet} onClose={() => setShowSend(false)} onSent={(count) => {
          alert(`Email sent to ${count} recipient${count !== 1 ? 's' : ''}!`);
          setShowSend(false);
        }} />
      )}
      {ConfirmDialog}
    </div>
  );
}
